import type {
  ScheduleClassOption,
  ScheduleOccurrenceDraft,
  ScheduleSnapshotDetail,
  ScheduleSnapshotDiagnoseRequest,
  ScheduleSnapshotDiagnosis,
  ScheduleSnapshotDraftRequest,
  ScheduleSnapshotOccurrence,
  ScheduleSnapshotOccurrenceRequest,
  ScheduleSnapshotSummary,
  StagedCourse
} from '@/types'
import { http } from '../request'
import {
  buildClassOptionKey,
  compareClassNames,
  createOccurrenceKey,
  toIsoString,
  toScheduleConflict,
  toScheduleSlot,
  toTeacherSchedule
} from './shared'

type SnapshotOccurrencePayload = {
  taskActivityId: number
  occurrenceIndex: number
  placed: boolean
  dayOfWeek: number | undefined
  period: number | undefined
  roomId: number | undefined
}

export const snapshotApi = {
  async listSnapshots(taskId: number): Promise<ScheduleSnapshotSummary[]> {
    const response = await http.get<ScheduleSnapshotSummary[]>(`/schedule/tasks/${taskId}/snapshots`)
    return (Array.isArray(response.data) ? response.data : []).map(toSnapshotSummary)
  },

  async getSnapshot(taskId: number, snapshotId: number): Promise<ScheduleSnapshotDetail> {
    const response = await http.get<ScheduleSnapshotDetail>(`/schedule/tasks/${taskId}/snapshots/${snapshotId}`)
    return toSnapshotDetail(response.data)
  },

  async renameSnapshot(
    taskId: number,
    snapshotId: number,
    name: string
  ): Promise<ScheduleSnapshotSummary> {
    const response = await http.put<ScheduleSnapshotSummary>(
      `/schedule/tasks/${taskId}/snapshots/${snapshotId}/rename`,
      { name }
    )
    return toSnapshotSummary(response.data)
  },

  async diagnoseSnapshot(
    taskId: number,
    payload: ScheduleSnapshotDiagnoseRequest
  ): Promise<ScheduleSnapshotDiagnosis> {
    const response = await http.post<ScheduleSnapshotDiagnosis>(
      `/schedule/tasks/${taskId}/snapshots/diagnose`,
      payload
    )
    return toSnapshotDiagnosis(response.data)
  },

  async saveSnapshot(
    taskId: number,
    payload: ScheduleSnapshotDraftRequest
  ): Promise<ScheduleSnapshotDetail> {
    const response = await http.post<ScheduleSnapshotDetail>(`/schedule/tasks/${taskId}/snapshots`, {
      name: payload.name,
      remark: payload.remark,
      occurrences: payload.occurrences.map(toOccurrenceRequest)
    })
    return toSnapshotDetail(response.data)
  },

  async updateSnapshot(
    taskId: number,
    snapshotId: number,
    payload: ScheduleSnapshotDraftRequest
  ): Promise<ScheduleSnapshotDetail> {
    const response = await http.put<ScheduleSnapshotDetail>(`/schedule/tasks/${taskId}/snapshots/${snapshotId}`, {
      name: payload.name,
      remark: payload.remark,
      occurrences: payload.occurrences.map(toOccurrenceRequest)
    })
    return toSnapshotDetail(response.data)
  },

  async deleteSnapshot(taskId: number, snapshotId: number): Promise<void> {
    await http.delete<void>(`/schedule/tasks/${taskId}/snapshots/${snapshotId}`)
  }
}

function toTeacherIdList(payload: unknown): number[] {
  return Array.isArray(payload)
    ? payload.map((item) => Number(item)).filter((id) => Number.isFinite(id) && id > 0)
    : []
}

function toTeacherNameList(payload: unknown): string[] {
  return Array.isArray(payload)
    ? payload.map((item) => String(item)).filter((name) => name !== '')
    : []
}

function toSnapshotOccurrence(payload: unknown): ScheduleSnapshotOccurrence {
  const occurrence = payload as Record<string, unknown>
  const teacherId = occurrence.teacherId != null ? Number(occurrence.teacherId) : null
  const teacherIds = toTeacherIdList(occurrence.teacherIds)
  const teacherNames = toTeacherNameList(occurrence.teacherNames)
  return {
    taskActivityId: Number(occurrence.taskActivityId ?? 0),
    occurrenceIndex: Number(occurrence.occurrenceIndex ?? 0),
    placed: Boolean(occurrence.placed),
    dayOfWeek: occurrence.dayOfWeek != null ? Number(occurrence.dayOfWeek) : null,
    period: occurrence.period != null ? Number(occurrence.period) : null,
    duration: Number(occurrence.duration ?? 1),
    courseId: occurrence.courseId != null ? Number(occurrence.courseId) : null,
    courseName: String(occurrence.courseName ?? ''),
    classId: occurrence.classId != null ? Number(occurrence.classId) : null,
    className: String(occurrence.className ?? ''),
    subgroupId: occurrence.subgroupId != null ? Number(occurrence.subgroupId) : null,
    subgroupName: occurrence.subgroupName != null ? String(occurrence.subgroupName) : null,
    teacherId,
    teacherName: teacherNames.length > 0
      ? teacherNames.join('、')
      : String(occurrence.teacherName ?? ''),
    teacherIds: teacherIds.length > 0
      ? teacherIds
      : teacherId != null ? [teacherId] : [],
    teacherNames,
    roomId: occurrence.roomId != null ? Number(occurrence.roomId) : null,
    roomName: occurrence.roomName != null ? String(occurrence.roomName) : null
  }
}

function toSnapshotSummary(payload: unknown): ScheduleSnapshotSummary {
  const snapshot = payload as Record<string, unknown>
  const snapshotStatus = typeof snapshot.snapshotStatus === 'string' ? snapshot.snapshotStatus : undefined
  return {
    id: Number(snapshot.id ?? 0),
    taskId: Number(snapshot.taskId ?? 0),
    snapshotType: String(snapshot.snapshotType ?? ''),
    name: snapshot.name != null ? String(snapshot.name) : null,
    sourceRunId: snapshot.sourceRunId != null ? Number(snapshot.sourceRunId) : null,
    isPublished:
      snapshot.isPublished === true
      || snapshotStatus === 'SNAPSHOT_PUBLISHED'
      || snapshotStatus === 'SNAPSHOT_ACTIVATED',
    snapshotStatus:
      snapshotStatus === 'SNAPSHOT_UNPUBLISHED'
      || snapshotStatus === 'SNAPSHOT_PUBLISHED'
      || snapshotStatus === 'SNAPSHOT_ACTIVATED'
        ? snapshotStatus
        : undefined,
    remark: snapshot.remark != null ? String(snapshot.remark) : null,
    createdAt: toIsoString(snapshot.createdAt),
    updatedAt: toIsoString(snapshot.updatedAt)
  }
}

function toSnapshotDetail(payload: unknown): ScheduleSnapshotDetail {
  const snapshot = payload as Record<string, unknown>
  return {
    ...toSnapshotSummary(snapshot),
    occurrences: Array.isArray(snapshot.occurrences)
      ? snapshot.occurrences.map((item) => toSnapshotOccurrence(item))
      : [],
    classPreviews: Array.isArray(snapshot.classPreviews)
      ? snapshot.classPreviews.map((item) => {
          const preview = item as Record<string, unknown>
          return {
            classId: Number(preview.classId ?? 0),
            className: String(preview.className ?? ''),
            schedule: Array.isArray(preview.schedule)
              ? preview.schedule.map((slot) => toScheduleSlot(slot))
              : []
          }
        })
      : [],
    teacherPreviews: Array.isArray(snapshot.teacherPreviews)
      ? snapshot.teacherPreviews.map((item) => toTeacherSchedule(item))
      : [],
    unplacedOccurrences: Array.isArray(snapshot.unplacedOccurrences)
      ? snapshot.unplacedOccurrences.map((item) => toSnapshotOccurrence(item))
      : [],
    stagedCourses: Array.isArray(snapshot.stagedCourses)
      ? snapshot.stagedCourses.map((item) => toStagedCourse(item))
      : [],
    warnings: Array.isArray(snapshot.warnings)
      ? snapshot.warnings.map((item) => toScheduleConflict(item))
      : []
  }
}

function toSnapshotDiagnosis(payload: unknown): ScheduleSnapshotDiagnosis {
  const diagnosis = payload as Record<string, unknown>
  return {
    snapshotId: diagnosis.snapshotId != null ? Number(diagnosis.snapshotId) : null,
    runId: diagnosis.runId != null ? Number(diagnosis.runId) : null,
    conflicts: Array.isArray(diagnosis.conflicts)
      ? diagnosis.conflicts.map((item) => toScheduleConflict(item))
      : []
  }
}

function toStagedCourse(payload: unknown): StagedCourse {
  const course = payload as Record<string, unknown>
  return {
    id: String(course.id ?? course.occurrenceKey ?? ''),
    occurrenceKey: typeof course.occurrenceKey === 'string' ? course.occurrenceKey : undefined,
    taskActivityId: course.taskActivityId != null ? Number(course.taskActivityId) : null,
    occurrenceIndex: course.occurrenceIndex != null ? Number(course.occurrenceIndex) : null,
    courseId: course.courseId != null ? Number(course.courseId) : null,
    courseName: String(course.courseName ?? ''),
    teacherId: course.teacherId != null ? Number(course.teacherId) : null,
    teacherName: String(course.teacherName ?? ''),
    classId: course.classId != null ? Number(course.classId) : null,
    className: String(course.className ?? ''),
    subgroupId: course.subgroupId != null ? Number(course.subgroupId) : null,
    subgroupName: course.subgroupName != null ? String(course.subgroupName) : null,
    remainingHours: course.remainingHours != null ? Number(course.remainingHours) : null,
    totalHours: course.totalHours != null ? Number(course.totalHours) : null,
    originalDay: course.originalDay != null ? Number(course.originalDay) : null,
    originalPeriod: course.originalPeriod != null ? Number(course.originalPeriod) : null
  }
}

function snapshotToDraftOccurrences(snapshot: ScheduleSnapshotDetail): ScheduleOccurrenceDraft[] {
  const occurrences: ScheduleOccurrenceDraft[] = snapshot.occurrences.map((item) => ({
    key: createOccurrenceKey(item.taskActivityId, item.occurrenceIndex),
    taskActivityId: item.taskActivityId,
    occurrenceIndex: item.occurrenceIndex,
    courseId: item.courseId,
    courseName: item.courseName,
    classId: item.classId,
    className: item.className,
    subgroupId: item.subgroupId ?? null,
    subgroupName: item.subgroupName ?? null,
    teacherId: item.teacherId,
    teacherName: item.teacherName,
    teacherIds: item.teacherIds ?? null,
    teacherNames: item.teacherNames ?? null,
    duration: item.duration,
    placed: item.placed,
    dayOfWeek: item.dayOfWeek,
    period: item.period,
    roomId: item.roomId,
    roomName: item.roomName
  }))

  const unplacedKeys = new Set(occurrences.filter((o) => !o.placed).map((o) => o.key))
  for (const unplaced of snapshot.unplacedOccurrences) {
    const key = createOccurrenceKey(unplaced.taskActivityId, unplaced.occurrenceIndex)
    if (!unplacedKeys.has(key)) {
      occurrences.push({
        key,
        taskActivityId: unplaced.taskActivityId,
        occurrenceIndex: unplaced.occurrenceIndex,
        courseId: unplaced.courseId,
        courseName: unplaced.courseName,
        classId: unplaced.classId,
        className: unplaced.className,
        subgroupId: unplaced.subgroupId ?? null,
        subgroupName: unplaced.subgroupName ?? null,
        teacherId: unplaced.teacherId,
        teacherName: unplaced.teacherName,
        teacherIds: unplaced.teacherIds ?? null,
        teacherNames: unplaced.teacherNames ?? null,
        duration: unplaced.duration,
        placed: false,
        dayOfWeek: null,
        period: null,
        roomId: null,
        roomName: null
      })
    }
  }

  return occurrences
}

export function buildDraftOccurrencesFromSnapshot(snapshot: ScheduleSnapshotDetail): ScheduleOccurrenceDraft[] {
  return snapshotToDraftOccurrences(snapshot)
}

function toOccurrenceRequest(
  occurrence: ScheduleSnapshotOccurrenceRequest
): SnapshotOccurrencePayload {
  return {
    taskActivityId: occurrence.taskActivityId,
    occurrenceIndex: occurrence.occurrenceIndex,
    placed: occurrence.placed,
    dayOfWeek: occurrence.dayOfWeek ?? undefined,
    period: occurrence.period ?? undefined,
    roomId: occurrence.roomId ?? undefined
  }
}

export function sortStagedCourses(
  courses: StagedCourse[],
  classOptions: ScheduleClassOption[]
): StagedCourse[] {
  const orderMap = new Map(classOptions.map((option, index) => [option.key, index]))

  return [...courses].sort((left, right) => {
    const leftKey =
      left.classId != null ? buildClassOptionKey(left.classId, left.subgroupId) : null
    const rightKey =
      right.classId != null ? buildClassOptionKey(right.classId, right.subgroupId) : null
    const unassignedOrder = Number.MAX_SAFE_INTEGER
    const leftOrder = leftKey != null ? (orderMap.get(leftKey) ?? unassignedOrder) : unassignedOrder + 1
    const rightOrder = rightKey != null ? (orderMap.get(rightKey) ?? unassignedOrder) : unassignedOrder + 1
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder
    }

    const dayDiff = (left.originalDay ?? 99) - (right.originalDay ?? 99)
    if (dayDiff !== 0) {
      return dayDiff
    }

    const periodDiff = (left.originalPeriod ?? 99) - (right.originalPeriod ?? 99)
    if (periodDiff !== 0) {
      return periodDiff
    }

    return compareClassNames(left.courseName, right.courseName)
  })
}
